import {Component, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Observable, Subject} from "rxjs";
import {map} from "rxjs/operators";
import {LayoutMode, LayoutService} from "./shared/layout.service";
import {MyToastComponent} from '../shared/components/toast/component/my-toast.component';
import {IconComponent} from '../shared/components/icon/icon.component';
import {TopbarComponent} from './shared/topbar/topbar.component';
import {LeftSidebarComponent} from './shared/left-sidebar/left-sidebar.component';
import {SimplebarAngularModule} from 'simplebar-angular';
import {AsyncPipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {LoggingService} from "../core/service/logging.service";
import {AuthenticationService} from "../core/service/auth.service";
import {HandoffService} from '../core/handoff/handoff.service';
import {SubjectTypeConfigService} from '../shared/service/subject-type-config/subject-type-config.service';
import {RecipeService} from '../apps/recipes/shared/recipe.service';
import {filter, takeUntil} from "rxjs/operators";
import {RightPanelService} from './shared/right-panel/right-panel.service';
import {WhatsNewService} from '../apps/whats-new/shared/service/whats-new.service';
import {environment} from '../../environments/environment';
import {AiFeedbackService} from '../core/ai-feedback/ai-feedback.service';
import {TranslateModule} from '@ngx-translate/core';
import {ClarityService} from '../core/service/clarity.service';
import {DwellTrackerService} from '../core/service/dwell-tracker.service';
import {UpgradeModalService} from '../shared/components/upgrade-modal/upgrade-modal.service';
import {BrandService} from '../shared/service/personalization/brand.service';
import {UsageStore} from '../shared/statistics/usage.store';

@Component({
    selector: 'app-layout-container',
    templateUrl: './layout-container.component.html',
    styleUrls: ['./layout-container.component.scss'],
    imports: [MyToastComponent, TopbarComponent, LeftSidebarComponent, SimplebarAngularModule, NgClass, NgTemplateOutlet, RouterOutlet, AsyncPipe, IconComponent, TranslateModule]
})
export class LayoutContainerComponent implements OnInit, OnDestroy {
    layoutMode$: Observable<LayoutMode>;
    sidebarCollapsed$: Observable<boolean>;

    /** CSS třídy aplikované na wrapper uvnitř simplebar – mění se dle layoutMode$ */
    contentClass$: Observable<string>;

    /** True pokud stránka sama spravuje scroll (simplebar nesmí scrollovat) */
    isFluidMode$: Observable<boolean>;

    private destroy$ = new Subject<void>();
    private subscriptions: string[] = [];
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly layoutService: LayoutService,
        private router: Router,
        private logger: LoggingService,
        private authService: AuthenticationService,
        private readonly handoffService: HandoffService,
        private subjectTypeConfigService: SubjectTypeConfigService,
        private recipeService: RecipeService,
        protected readonly rightPanel: RightPanelService,
        private readonly whatsNewService: WhatsNewService,
        private readonly aiFeedbackService: AiFeedbackService,
        private clarityService: ClarityService,
        private dwellTracker: DwellTrackerService,
        private readonly upgradeModalService: UpgradeModalService,
        private brandService: BrandService,
        private readonly usageStore: UsageStore,
    ) {
        this.layoutMode$ = this.layoutService.layoutMode$;
        this.sidebarCollapsed$ = this.layoutService.sidebarCollapsed$;

        this.contentClass$ = this.layoutMode$.pipe(
            map(mode => {
                switch (mode) {
                    case 'fluid':    return 'h-full overflow-hidden flex flex-col';
                    case 'wide':     return 'h-full w-full';
                    case 'centered': return 'container h-full mx-auto';
                    default:         return 'container h-full mx-auto';
                }
            })
        );

        this.isFluidMode$ = this.layoutMode$.pipe(map(mode => mode === 'fluid'));
    }

    ngOnInit(): void {
        // Registruje listener 'show-upgrade-modal' + preload produktů. Musí běžet odsud (ne z
        // konstruktoru služby), aby auth/HTTP-závislá inicializace neběžela během DI konstrukce
        // interceptor chainu → NG0200. UpgradeModalService se nikde jinde neinjektuje, takže
        // tohle je jediné místo, kde vznikne a začne naslouchat.
        this.upgradeModalService.init();

        // Spotřeba pro topbar chip. Stejný důvod jako u upgradeModalService: store visí přes
        // StatisticsService na HttpClient, takže se nesmí rozjet během stavby interceptor
        // chainu (NG0200) ani z APP_INITIALIZER (běží před auth → 401).
        this.usageStore.init();

        if (environment.whatsNewEnabled) {
            this.whatsNewService.init();
        }
        this.aiFeedbackService.loadDismissState();

        // Per-route dwell-time tracking (which features users actually use).
        this.dwellTracker.init();

        this.subjectTypeConfigService.load().subscribe();

        this.recipeService.load().subscribe();

        // Track active subscriptions reactively so navigation logs always carry the
        // user's current subscription state (a mid-session upgrade is reflected on
        // the very next navigation event, not snapshotted at component init).
        this.authService.activeSubscriptions$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(subs => {
                this.subscriptions = subs.map(value => value.subscription);
                const user = this.authService.currentUser();
                this.clarityService.update(this.subscriptions, user?.uid ?? '', user?.email ?? '', user?.displayName ?? null);
            });

        // Subscribe to router events and log navigation
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.destroy$)
        ).subscribe((event: NavigationEnd) => {
            this.logger.info('NAVIGATION_EVENT: Route change', {
                url: event.urlAfterRedirects,
                id: event.id,
                subscriptions: this.subscriptions.join(',')
            });
            // Clarity detects SPA navigation automatically via its pushState patch
        });

        this.brandService.load().subscribe();

        // Předání z webu (spec 2026-09-15 §6.2): jen uživatel s předplatným — bez něj by kaskáda narazila
        // na subscribersGuard a klíč by propadl; nový uživatel dostane spouštěč z SubscriptionAwaitComponent.
        void this.authService.getCurrentUserActiveSubscriptions().then(subscriptions => {
            if (subscriptions.length > 0) {
                return this.handoffService.dispatch('layout-init');
            }
            return false;
        }).catch(error => this.logger.warn('Handoff dispatch failed (layout-init)', {error: String(error)}));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

}
